import api from './api';
import { UnitAvailability, AvailabilityStatusText } from '@/types';

interface RawAvailabilityItem {
  id?: string | number;
  siteName: string;
  roomNumber: string;
  intervalNum: string;
  endDate: string;
  years: any;
  status: string;
  [key: string]: any;
}

export interface RawUnitStub {
  resort: string;
  number: string;
}

export interface UnitAvailabilityResult {
  availabilities: UnitAvailability[];
  units: RawUnitStub[];
}

function transformAvailabilities(raw: RawAvailabilityItem[]): UnitAvailabilityResult {
  const currentYear = new Date().getFullYear();
  const availabilities: UnitAvailability[] = [];
  const seen = new Set<string>();
  const units: RawUnitStub[] = [];

  for (const item of raw) {
    // Collect every unique (resort, roomNumber) for the dropdown
    const key = `${item.siteName}|${item.roomNumber}`;
    if (!seen.has(key)) {
      seen.add(key);
      units.push({ resort: item.siteName, number: item.roomNumber });
    }

    const week = Number(item.intervalNum);
    if (isNaN(week)) continue;

    const endYear = item.endDate ? new Date(item.endDate).getFullYear() : currentYear + 15;
    const safeEndYear = isNaN(endYear) ? currentYear + 15 : endYear;

    if (item.status === 'available') {
      const reservedYears = new Set<number>(
        Array.isArray(item.years)
          ? item.years.map(Number).filter((y: number) => !isNaN(y))
          : []
      );
      for (let year = currentYear; year <= safeEndYear; year++) {
        availabilities.push({
          id: item.id,
          resort: item.siteName,
          number: item.roomNumber,
          week,
          year,
          status: reservedYears.has(year)
            ? AvailabilityStatusText.Reserved
            : AvailabilityStatusText.Available,
        });
      }
    } else if (item.status === 'reserved') {
      const reservedYears: number[] = Array.isArray(item.years)
        ? item.years.map(Number).filter((y: number) => !isNaN(y))
        : Array.from({ length: safeEndYear - currentYear + 1 }, (_, i) => currentYear + i);
      for (const year of reservedYears) {
        availabilities.push({
          id: item.id,
          resort: item.siteName,
          number: item.roomNumber,
          week,
          year,
          status: AvailabilityStatusText.Reserved,
        });
      }
    }
  }

  return { availabilities, units };
}

export const getUnitAvailability = async (): Promise<UnitAvailabilityResult> => {
  try {
    const response = await api.get('/api/clc/unit-availability');
    const raw: RawAvailabilityItem[] = response.data?.unitAvailability?.availabilities || [];
    return Array.isArray(raw) ? transformAvailabilities(raw) : { availabilities: [], units: [] };
  } catch (error) {
    console.error('Error fetching unit availability:', error);
    return { availabilities: [], units: [] };
  }
};
